// Mantine Form Schema and Validation for TaskFormWizard
import { useCallback } from 'react';
import { useForm, UseFormInput, UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { Priority } from '../../../../types';
import type { InlineRequirement } from '../types/data/InlineRequirement';
import { WIZARD_CONSTANTS } from '../types/constants/constants';
import { FILE_VALIDATION_CONFIG } from '@/utils/fileValidation';
import type { TaskStepLogic } from './validation/stepValidation';

// Combined form data interface for Mantine form
// Note: anchorPoint is managed by Zustand (tasks.formAnchorPoint), not Mantine form
export interface MantineWizardFormData {
  // Step 1 fields
  title: string;
  priority: Priority;
  assignedToId: string;
  startDate?: string;
  dueDate?: string;
  location?: string;
  locationFromMap?: boolean;

  // Step 2 fields
  description: string;
  attachments: File[];

  // Step 3 fields
  requirements: InlineRequirement[];

  // Step 4 fields
  relatedTaskIds: string[];
  requireRelatedTasksCompletion: boolean;

  // Meta fields
  currentStep: number;
}

// Return type for useTaskWizardForm hook
export interface UseTaskWizardFormReturn {
  form: UseFormReturnType<MantineWizardFormData>;
  /** Jump to a section by its 1-indexed position (free navigation). */
  goToStep: (step: number) => boolean;
}

// Default form values
const getDefaultFormValues = (): MantineWizardFormData => ({
  title: '',
  priority: Priority.Medium,
  assignedToId: '',
  startDate: '',
  dueDate: '',
  location: '',
  locationFromMap: false,
  description: '',
  attachments: [],
  requirements: [],
  relatedTaskIds: [],
  requireRelatedTasksCompletion: false,
  currentStep: 1,
});

// Validation rules factory that takes translation function
const getFormValidationRules = (
  t: (key: string) => string
): UseFormInput<MantineWizardFormData>['validate'] => ({
  // Step 1 validation
  title: (value: string) => {
    if (!value || value.trim().length === 0) {
      return t('validation.titleRequired');
    }
    if (value.trim().length < WIZARD_CONSTANTS.MIN_TITLE_LENGTH) {
      return t('validation.titleMinLength');
    }
    if (value.trim().length > WIZARD_CONSTANTS.MAX_TITLE_LENGTH) {
      return t('validation.titleMaxLength');
    }
    return null;
  },

  priority: (value: Priority) => {
    if (!value || !Object.values(Priority).includes(value)) {
      return t('validation.priorityRequired');
    }
    return null;
  },

  assignedToId: () => {
    // Assignee is optional (nullable GUID)
    return null;
  },

  // Step 2 validation - Description has max length validation
  description: (value: string) => {
    if (value && value.length > WIZARD_CONSTANTS.MAX_DESCRIPTION_LENGTH) {
      return t('validation.descriptionMaxLength');
    }
    return null;
  },

  attachments: (value: File[]) => {
    const oversizedFiles = value.filter(
      (file) => file.size > FILE_VALIDATION_CONFIG.MAX_FILE_SIZE
    );
    if (oversizedFiles.length > 0) {
      return t('validation.fileTooBig');
    }
    return null;
  },

  // Step 3 validation
  requirements: (value: InlineRequirement[]) => {
    if (value.length > WIZARD_CONSTANTS.MAX_REQUIREMENTS) {
      return t('validation.requirementsMaxCount');
    }

    const emptyRequirements = value.filter((req) => !req.text.trim());
    if (emptyRequirements.length > 0) {
      return t('validation.requirementRequired');
    }

    return null;
  },
});

// Custom hook to create and manage Mantine form for TaskWizard
export const useTaskWizardForm = (
  initialData: Partial<MantineWizardFormData> | undefined,
  steps: TaskStepLogic[]
): UseTaskWizardFormReturn => {
  const { t } = useTranslation();
  const totalSteps = steps.length;

  const form = useForm<MantineWizardFormData>({
    initialValues: {
      ...getDefaultFormValues(),
      ...initialData,
    },
    validate: getFormValidationRules(t),
    validateInputOnBlur: true,
    validateInputOnChange: true, // Real-time validation - validates as user types
  });

  // Note: No useEffect needed - form reinitializes via key prop in TasksPage
  // TaskFormWizard remounts when key changes (different task/mode)

  // Free section navigation (tabs): jump to any valid section position. Submit
  // validates the whole form, so per-section gating is intentionally absent.
  const goToStep = useCallback(
    (step: number): boolean => {
      if (step < 1 || step > totalSteps) return false;
      form.setFieldValue('currentStep', step);
      return true;
    },
    [form, totalSteps]
  );

  return {
    form,
    goToStep,
  };
};
